/**
 * PATH: apps/qa/src/lib/evidence/evidencePayload.ts
 * FILE_ID: QAAPP-LIB-EVIDENCE-PAYLOAD-001
 * PURPOSE: Build the two-step "upload bytes, then link evidence" contract the
 *          QA evidence endpoint actually accepts.
 * WHY: Both the live camera and the offline flush used to POST multipart with a
 *      `file` part. EvidenceCreateSerializer has no file field, so DRF dropped
 *      it silently and stored evidence with no image; the offline path also
 *      aimed at /api/qa/evidence/upload/, which is not a registered route.
 *      Bytes go to QDrive first, then the returned asset id is linked.
 * FLOW: file -> QDRIVE_UPLOAD_PATH -> {id} -> photoEvidenceBody -> QA_EVIDENCE_PATH
 */

/** Paths are API-root relative: the axios client already carries the /api
 *  prefix in its baseURL, while the offline flush uses fetch and prepends it. */
export const QDRIVE_UPLOAD_PATH = '/qdrive/files/upload/'
export const QA_EVIDENCE_PATH = '/qa/evidence/'

export type EvidenceEntityType = 'inspection' | 'area' | 'item' | 'item_instance' | 'ncr'

export interface EvidenceTarget {
  /** Entity the capture hangs off, as named by the caller's own vocabulary. */
  type?: EvidenceEntityType
  id?: string
  /** Always sent when known: an item-only payload fails the serializer's
   *  "must be linked to an inspection, floor, or area" rule. */
  inspectionId?: string
}

export interface PhotoEvidenceBody {
  type: 'photo'
  asset_id: string
  area?: string
  item_instance?: string
  inspection?: string
  note_text?: string
  description?: string
  device_metadata?: Record<string, unknown>
}

/** Map a caller's entity vocabulary onto the serializer's link fields. */
export function evidenceTargetFields(target: EvidenceTarget): Record<string, string> {
  const fields: Record<string, string> = {}
  if (target.id) {
    if (target.type === 'area') fields.area = target.id
    else if (target.type === 'item' || target.type === 'item_instance') {
      fields.item_instance = target.id
    } else if (target.type === 'inspection') fields.inspection = target.id
  }
  // An item_instance alone is not a link the serializer accepts, and the
  // inspection is also what scopes the row for the reports that read it.
  if (target.inspectionId && !fields.inspection) fields.inspection = target.inspectionId
  return fields
}

export function photoEvidenceBody(input: {
  target: EvidenceTarget
  assetId: string
  noteText?: string
  description?: string
  deviceMetadata?: Record<string, unknown>
}): PhotoEvidenceBody {
  const body: PhotoEvidenceBody = {
    type: 'photo',
    asset_id: input.assetId,
    ...evidenceTargetFields(input.target),
  }
  if (input.noteText) body.note_text = input.noteText
  if (input.description) body.description = input.description
  if (input.deviceMetadata && Object.keys(input.deviceMetadata).length > 0) {
    body.device_metadata = input.deviceMetadata
  }
  return body
}

/** Build the multipart body for the QDrive upload leg. */
export function assetUploadForm(file: Blob, fileName: string, jobId?: number | string): FormData {
  const form = new FormData()
  form.append('file', file, fileName)
  if (jobId !== undefined && jobId !== null && `${jobId}`.length > 0) {
    form.append('job_id', String(jobId))
  }
  return form
}
